import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import Config from './components/Config';
import HealthProfessionals from './components/Login/ProfessionalLogin';
import ProfessionalDashboard from './components/pages/dashboard/ProfessionalDashboard';
import PatientList from './components/pages/patients/PatientList';
import PatientDetail from './components/pages/patients/PatientDetail';
import AppointmentsPage from './components/pages/appointments/AppointmentsPage';
import TasksPage from './components/pages/tasks/TasksPage';
import ProtectedRoute, { AuthProvider } from './components/common/protectedRoute/ProtectedRoute';
import { TasksProvider } from './contexts/TasksContext';
import RecordsPage from './components/pages/records/RecordsPage';
import PatientTrendsPage from './components/pages/records/PatientTrendsPage';
import SubscriptionSuccess from './components/Subscription/SubscriptionSuccess';
import SubscriptionCancel from './components/Subscription/SubscriptionCancel';
import { SubscriptionProvider } from './contexts/context/SubscriptionContext';
import ProfessionalPublicProfile from './components/public/ProfessionalPublicProfile';
import SubscriptionPromo from './components/Subscription/SubscriptionPromo';
import { useSubscription } from './contexts/context/SubscriptionContext';
import Configuration from './components/pages/configuration/Configuration';
import IntercomProvider from './components/common/intercom/IntercomProvider';
import IntercomHider from './components/common/intercom/IntercomHider';
import IntercomDataUpdater from './components/common/intercom/IntercomDataUpdater';
import { trackIntercomEvent } from './components/common/intercom/intercomUtils';
import AGTFloatingChat from './components/utils/AGTFloatingChat';
import AppleSpinner from '../src/common/AppleSpinner';

// Componente para mostrar el spinner centralizado de carga
const FullScreenSpinner = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      backgroundColor: 'background.default'
    }}
  >
    <AppleSpinner sizeDesktop={80} sizeMobile={60} />
  </Box>
);

// Componente para controlar la visibilidad del AGTFloatingChat (simplificado)
const FloatingChatController = ({ isAuthenticated, hasValidAccess }) => {
  const location = useLocation();
  
  // Definir rutas públicas donde NO debe aparecer el chat
  const publicRoutes = [
    '/p/', // Perfiles públicos (cualquier ruta que comience con /p/)
    '/subscription/success',
    '/subscription/cancel',
    '/login',
    '/',
    '/intercom'
  ];
  
  // Verificar si la ruta actual es pública
  const isPublicRoute = publicRoutes.some(route => {
    if (route === '/p/') {
      // Para rutas dinámicas como /p/:slug, verificar si comienza con /p/
      return location.pathname.startsWith('/p/');
    }
    return location.pathname === route;
  });
  
  // No mostrar el chat si:
  // - No está autenticado
  // - No tiene acceso válido
  // - Está en una ruta pública
  if (!isAuthenticated || !hasValidAccess || isPublicRoute) {
    return null;
  }
  
  return <AGTFloatingChat />;
};

// Componente para manejar la lógica de suscripción de manera centralizada
const AppWithSubscription = ({ isAuthenticated, user, onLogout }) => {
  const { isPro, isLoading, hasSubscription, status, trialActive, refreshStatus } = useSubscription();
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);
  const location = useLocation(); // Agregar useLocation
  
  // Verificar suscripción una sola vez al montar
  useEffect(() => {
    if (!isLoading && !hasCheckedSubscription) {
      refreshStatus().finally(() => {
        setHasCheckedSubscription(true);
      });
    }
  }, [isLoading, hasCheckedSubscription, refreshStatus]);
  
  // Mostrar spinner mientras se verifica la suscripción
  if (isLoading || !hasCheckedSubscription) {
    return <FullScreenSpinner />;
  }
  
  // Determinar si tiene acceso válido
  const hasValidAccess = isPro && (trialActive || status === 'active' || status === 'trialing');
  
  // NUEVA LÓGICA: Permitir acceso a rutas de procesamiento de suscripción
  const isProcessingSubscription = location.pathname === '/subscription/success' || 
                                   location.pathname === '/subscription/cancel';
  
  // Si no tiene acceso válido Y NO está procesando una suscripción, mostrar la promoción
  if (!hasValidAccess && !isProcessingSubscription) {
    console.log("Acceso denegado. Estado:", { isPro, hasSubscription, status, trialActive });
    trackIntercomEvent('subscription_required', {
      status,
      trialActive,
      isPro,
      hasSubscription
    });
    return <SubscriptionPromo />;
  }
  
  // Si tiene acceso válido O está procesando suscripción, mostrar la aplicación completa
  return (
    <TasksProvider>
      <Routes>
        <Route path="/dashboard" element={
          <ProfessionalDashboard user={user} activePage="dashboard" onLogout={onLogout} />
        } />
        <Route path="/patients" element={
          <ProtectedRoute>
            <PatientList onLogout={onLogout} />
          </ProtectedRoute>
        } />
        <Route path="/patients/:patientId" element={
          <ProtectedRoute>
            <PatientDetail onLogout={onLogout} />
          </ProtectedRoute>
        } />
        <Route path="/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage onLogout={onLogout} />
          </ProtectedRoute>
        } />
        <Route path="/tasks" element={
          <ProtectedRoute>
            <TasksPage onLogout={onLogout} />
          </ProtectedRoute>
        } />
        <Route path="/records" element={
          <ProtectedRoute>
            <RecordsPage onLogout={onLogout} />
          </ProtectedRoute>
        } />
        <Route path="/records/trends/:patientId" element={
          <ProtectedRoute>
            <PatientTrendsPage onLogout={onLogout} />
          </ProtectedRoute>
        } />
        <Route path="/my-agt" element={
          <ProfessionalDashboard activePage="my-agt" onLogout={onLogout} />
        } />
        <Route path="/settings" element={
          <ProfessionalDashboard activePage="settings" onLogout={onLogout} />
        } />
        <Route path="/intercom" element={
          <ProfessionalDashboard activePage="intercom" onLogout={onLogout} />
        } />
        {/* RUTAS DE PROCESAMIENTO DE SUSCRIPCIÓN - No requieren validación de suscripción */}
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        <Route path="/p/:slug" element={<ProfessionalPublicProfile />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      
      {/* AGTFloatingChat controlado de manera centralizada */}
      <FloatingChatController 
        isAuthenticated={isAuthenticated} 
        hasValidAccess={hasValidAccess} 
      />
    </TasksProvider>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [darkMode,] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
  });

  const handleUpdateUser = (updatedUser) => {
    setUser(updatedUser);
    setIsAuthenticated(true);
    
    const token = localStorage.getItem('agthealth_token');
    if (token) {
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (tokenData.exp && tokenData.exp > currentTime) {
          setIsAuthenticated(true);
          console.log("Usuario autenticado correctamente después del login");
          if (updatedUser && updatedUser.id) {
            trackIntercomEvent('user_login', {
              user_id: updatedUser.id,
              login_method: 'credentials'
            });
          }
        }
      } catch (error) {
        console.error("Error al procesar token después del login:", error);
      }
    }
  };

  // Función para verificar autenticación
  const checkAuthentication = () => {
    const token = localStorage.getItem('agthealth_token');
    console.log("Verificando autenticación - Token presente:", !!token);
  
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setIsCheckingAuth(false);
      return false;
    }
  
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
  
      if (tokenData.exp && tokenData.exp < currentTime) {
        console.log("Token expirado");
        localStorage.removeItem('agthealth_token');
        setIsAuthenticated(false);
        setUser(null);
        setIsCheckingAuth(false);
        return false;
      }
  
      console.log("Token decodificado:", tokenData);
  
      const userData = {
        id: tokenData.id || tokenData.sub || tokenData.userId || tokenData.user_id,
        email: tokenData.email,
        firstName: tokenData.firstName || tokenData.name || tokenData.given_name || '',
        lastName: tokenData.lastName || tokenData.family_name || '',
      };
  
      if (!userData.id && userData.email) {
        userData.id = userData.email;
      }
  
      console.log("Datos de usuario extraídos:", userData);
  
      setUser(userData);
      setIsAuthenticated(true);
      setIsCheckingAuth(false);
      return true;
    } catch (error) {
      console.error("Error al verificar token:", error);
      localStorage.removeItem('agthealth_token');
      setIsAuthenticated(false);
      setUser(null);
      setIsCheckingAuth(false);
      return false;
    }
  };
  
  useEffect(() => {
    console.log("App.js - Verificando autenticación inicial");
    
    const verifyAuth = () => {
      const isAuth = checkAuthentication();
      console.log("Verificación de autenticación completada:", isAuth);
    };
    
    verifyAuth();
    
    const handleStorageChange = (e) => {
      if (e.key === 'agthealth_token') {
        console.log("Token cambió en localStorage, verificando autenticación");
        verifyAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function() {
      originalSetItem.apply(this, arguments);
      const event = new Event('localStorageChange');
      document.dispatchEvent(event);
    };
    
    const handleLocalChange = () => {
      verifyAuth();
    };
    
    document.addEventListener('localStorageChange', handleLocalChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('localStorageChange', handleLocalChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  useEffect(() => {
    console.log("App.js - Estado de autenticación cambió:", isAuthenticated);
  }, [isAuthenticated]);

  const handleLogout = () => {
    trackIntercomEvent('user_logout');
    localStorage.removeItem('agthealth_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Mostrar AppleSpinner mientras se verifica la autenticación
  if (isCheckingAuth) {
    return <FullScreenSpinner />;
  }

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SubscriptionProvider>
          <IntercomProvider user={user} />
          <IntercomHider />
          {isAuthenticated && <IntercomDataUpdater updateInterval={15 * 60 * 1000} />}
          <AuthProvider isAuthenticated={isAuthenticated} isCheckingAuth={isCheckingAuth}>
            {!isAuthenticated ? (
              <Routes>
                <Route path="/login" element={<HealthProfessionals onUpdateUser={handleUpdateUser} />} />
                <Route path="/" element={<Config onUpdateUser={handleUpdateUser} />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                <Route path="/p/:slug" element={<ProfessionalPublicProfile />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
              <AppWithSubscription 
                isAuthenticated={isAuthenticated}
                user={user}
                onLogout={handleLogout}
              />
            )}
          </AuthProvider>
        </SubscriptionProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;